/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import ReactDOM from 'react-dom';
import { decorate, observable, computed, action, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { Icon } from 'semantic-ui-react';

import InstanceConfigPicker, { getConfigCost } from './InstanceConfigPicker';
import {
  formatRateDifference,
  getInstanceFamily,
  isHyperthreadingLockedInstanceType,
  parseConfigSpecs,
} from '../../../helpers/instance-config';
import { regionDisplayNames } from '../../../models/constants/aws-regions';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

/**
 * Maps a compatible instance type entry from either API into the shape the picker uses. The SC API
 * returns `{ envTypeConfigId, name, instanceType, desc, estimatedCostInfo, isCurrent }`; the
 * built-in API returns `{ size, title, desc }` and leaves the current size out.
 */
function toPickerConfig(entry) {
  const id = entry.envTypeConfigId || entry.id || entry.size;
  return {
    id,
    name: entry.name || entry.title || id,
    instanceType: String(entry.instanceType || entry.size || '').split('/')[0],
    desc: entry.desc || '',
    estimatedCostInfo: entry.estimatedCostInfo || '',
    isCurrent: !!entry.isCurrent,
  };
}

/**
 * A shared modal component for changing the instance type of an EC2 workspace.
 * Works with both SC and built-in stacks via function props for API calls.
 *
 * Props:
 *  - open: boolean controlling modal visibility
 *  - onClose: callback to close the modal
 *  - environment: the environment object (must have .id and .enableHyperthreading)
 *  - onConfirm: callback(selectedConfigId, enableHyperthreading) invoked on confirm
 *  - getCompatibleInstanceTypes: async function(envId) that returns compatible configs
 */
class ChangeInstanceTypeModal extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.compatibleConfigs = [];
      this.selectedConfigId = '';
      this.loading = false;
      this.submitting = false;
      this.error = '';
      this.enableHyperthreading = false;
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this.fetchCompatibleConfigs();
    }
  }

  get region() {
    const env = this.props.environment || {};
    return env.displayRegion || env.targetRegion || process.env.REACT_APP_AWS_REGION || '';
  }

  get currentConfig() {
    return this.compatibleConfigs.find(config => config.isCurrent);
  }

  get selectedConfig() {
    return this.compatibleConfigs.find(config => config.id === this.selectedConfigId);
  }

  get selectableConfigs() {
    return this.compatibleConfigs.filter(config => !config.isCurrent);
  }

  fetchCompatibleConfigs = async () => {
    const { environment, getCompatibleInstanceTypes } = this.props;
    runInAction(() => {
      this.loading = true;
      this.error = '';
      this.compatibleConfigs = [];
      this.selectedConfigId = '';
      // Start from the workspace's own setting. The workflow records whatever is sent, so a default
      // of off quietly turned hyperthreading off for anyone who resized without touching the toggle.
      this.enableHyperthreading = !!(environment && environment.enableHyperthreading);
    });

    try {
      const configs = await getCompatibleInstanceTypes(environment.id);
      runInAction(() => {
        this.compatibleConfigs = (configs || []).map(toPickerConfig);
        this.loading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err.message || 'Failed to load compatible instance types.';
        this.loading = false;
      });
    }
  };

  handleSelectConfig = config => {
    // The hyperthreading choice is kept across selections rather than reset each time.
    this.selectedConfigId = config.id;
  };

  handleHyperthreadingToggle = () => {
    this.enableHyperthreading = !this.enableHyperthreading;
  };

  handleConfirm = async () => {
    runInAction(() => {
      this.submitting = true;
      this.error = '';
    });

    try {
      await this.props.onConfirm(this.selectedConfigId, this.enableHyperthreading);
      runInAction(() => {
        this.submitting = false;
      });
      this.props.onClose();
    } catch (err) {
      runInAction(() => {
        this.error = err.message || 'Failed to change instance type. Please try again.';
        this.submitting = false;
      });
    }
  };

  handleClose = () => {
    if (!this.submitting) {
      this.props.onClose();
    }
  };

  renderLoading() {
    return (
      <div className="cit-modal-loading">
        <Icon name="spinner" loading size="large" />
        <span>Loading compatible instance types...</span>
      </div>
    );
  }

  renderError() {
    if (!this.error) return null;
    return (
      <div className="workspace-alert workspace-alert-error cit-modal-error" role="alert">
        <Icon name="exclamation triangle" />
        <div>
          <strong>Error:</strong> {this.error}
        </div>
      </div>
    );
  }

  renderEmptyState() {
    return (
      <div className="cit-modal-empty">
        <Icon name="info circle" style={{ color: '#2563eb', fontSize: '1.2em' }} />
        <div>
          <strong>No Compatible Instance Types</strong>
          <p style={{ margin: '4px 0 0', color: '#6b7280' }}>
            There are no alternative instance types available for this workspace.
          </p>
        </div>
      </div>
    );
  }

  renderCurrentConfig() {
    const current = this.currentConfig;
    if (!current) return null;
    const specs = parseConfigSpecs(current.desc);
    const cost = getConfigCost(current, this.region);
    const specParts = specs.hasSpecs
      ? [`${specs.cpus} CPU`, specs.threads ? `${specs.threads} threads` : null, specs.memory]
      : [specs.summary];
    const details = [current.instanceType, ...specParts].filter(Boolean).join(' · ');

    // One type size throughout; only the config name is emphasised.
    return (
      <div className="cit-modal-current" data-testid="cit-current-config">
        <span className="cit-modal-current-label">Current config</span>
        <span className="cit-modal-current-name">{current.name}</span>
        {details && <span className="cit-modal-current-details">{details}</span>}
        <span className="cit-modal-current-cost">{cost.text}</span>
      </div>
    );
  }

  renderBody() {
    if (this.loading) return this.renderLoading();
    if (this.compatibleConfigs.length === 0) {
      return (
        <>
          {this.renderError()}
          {!this.error && this.renderEmptyState()}
        </>
      );
    }

    const firstSelectable = this.selectableConfigs[0];
    const initialConfigId = (this.currentConfig || firstSelectable || {}).id;

    return (
      <>
        {this.renderError()}
        {this.renderCurrentConfig()}
        <InstanceConfigPicker
          configs={this.compatibleConfigs}
          selectedId={this.selectedConfigId}
          onSelect={this.handleSelectConfig}
          initialConfigId={initialConfigId}
          groupName="cit-config"
          region={this.region}
          costHeader="Est. Cost"
          disabled={this.submitting}
          scrollable
        />
        {/* Only SC workspaces carry a region, and only the SC API filters the list by it. */}
        {(this.props.environment || {}).displayRegion && (
          <p className="cit-modal-region-note" data-testid="cit-region-note">
            Workspaces can&apos;t be moved between regions, so only {regionDisplayNames[this.region] || this.region}{' '}
            configurations are shown. For a configuration in another region, create a new workspace there.
          </p>
        )}
        {this.renderCpuOptions()}
      </>
    );
  }

  renderSummary() {
    const selected = this.selectedConfig;
    if (!selected) {
      return (
        <div className="cit-modal-summary cit-modal-summary-empty" data-testid="cit-summary">
          Choose a new configuration to continue.
        </div>
      );
    }

    const current = this.currentConfig;
    const newCost = getConfigCost(selected, this.region);
    const currentCost = current ? getConfigCost(current, this.region) : null;
    const canCompare = currentCost && currentCost.rate !== null && newCost.rate !== null;
    const difference = canCompare ? newCost.rate - currentCost.rate : 0;

    return (
      <div className="cit-modal-summary" data-testid="cit-summary" aria-live="polite">
        {current && (
          <>
            <span className="cit-modal-summary-from">{current.name}</span>
            <Icon name="arrow right" className="cit-modal-summary-arrow" aria-hidden="true" />
          </>
        )}
        <span className="cit-modal-summary-to">{selected.name}</span>
        {canCompare && (
          <span
            className={`cit-modal-cost-diff${difference > 0 ? ' up' : ''}${difference < 0 ? ' down' : ''}`}
            data-testid="cit-cost-diff"
          >
            {formatRateDifference(difference)}
          </span>
        )}
      </div>
    );
  }

  // Sits under the list rather than in the footer, so it reads as a setting of the new instance.
  // Only the list scrolls, so this stays in view.
  renderCpuOptions() {
    return (
      <section className="cit-modal-section" aria-labelledby="cit-modal-cpu-title" data-testid="cit-cpu-options">
        <h3 className="cit-modal-section-title" id="cit-modal-cpu-title">
          <Icon name="microchip" />
          CPU Options
        </h3>
        {this.renderHyperthreading()}
      </section>
    );
  }

  renderHyperthreading() {
    const selected = this.selectedConfig;
    if (selected && isHyperthreadingLockedInstanceType(selected.instanceType)) {
      return (
        <div className="cit-modal-ht-locked" data-testid="cit-ht-locked">
          <Icon name="lock" style={iconStyle} />
          <span>
            Hyperthreading can&apos;t be changed on <strong>{getInstanceFamily(selected.instanceType)}</strong>{' '}
            instances.
          </span>
        </div>
      );
    }

    const hint = this.enableHyperthreading
      ? 'Each CPU core runs two threads, giving the full vCPU count.'
      : 'Each CPU core runs one thread, which halves the vCPU count.';

    return (
      <div className="cit-modal-ht">
        <label htmlFor="cit-modal-ht-toggle" className={`cit-modal-toggle${this.submitting ? ' disabled' : ''}`}>
          <input
            type="checkbox"
            role="switch"
            id="cit-modal-ht-toggle"
            className="cit-modal-toggle-input"
            checked={this.enableHyperthreading}
            aria-checked={this.enableHyperthreading}
            aria-describedby="cit-modal-ht-hint"
            onChange={this.handleHyperthreadingToggle}
            disabled={this.submitting}
            data-testid="cit-ht-toggle"
          />
          <span className="cit-modal-toggle-track" aria-hidden="true" />
          <span className="cit-modal-toggle-label">Hyperthreading</span>
        </label>
        <span id="cit-modal-ht-hint" className="cit-modal-hint" data-testid="cit-ht-hint">
          {hint}
        </span>
      </div>
    );
  }

  renderFooter() {
    const hasConfigs = this.compatibleConfigs.length > 0;
    const confirmDisabled = !this.selectedConfig || this.submitting || this.loading || !hasConfigs;

    return (
      <div className="cit-modal-footer">
        {hasConfigs && !this.loading && <div className="cit-modal-footer-info">{this.renderSummary()}</div>}
        <div className="cit-modal-footer-actions">
          <button
            type="button"
            className="workspace-action-btn workspace-action-btn-secondary"
            onClick={this.handleClose}
            disabled={this.submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="workspace-action-btn workspace-action-btn-restart"
            onClick={this.handleConfirm}
            disabled={confirmDisabled}
            data-testid="cit-confirm"
          >
            {this.submitting && <Icon name="spinner" loading style={iconStyle} />}
            {!this.submitting && <Icon name="check" style={iconStyle} />}
            Confirm
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { open } = this.props;
    if (!open) return null;

    return ReactDOM.createPortal(
      <>
        {/* The backdrop only mirrors the close button, which keyboard users reach instead. */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div className="cit-modal-backdrop" onClick={this.submitting ? undefined : this.handleClose} />
        <div className="cit-modal" role="dialog" aria-modal="true" aria-labelledby="cit-modal-title">
          <div className="cit-modal-header">
            <div className="cit-modal-header-title" id="cit-modal-title">
              <Icon name="exchange" style={{ color: '#2563eb', marginRight: '8px' }} />
              Change Instance Type
            </div>
            {!this.submitting && (
              <button type="button" className="cit-modal-close" onClick={this.handleClose} aria-label="Close">
                <Icon name="close" style={{ margin: 0 }} />
              </button>
            )}
          </div>
          <div className="cit-modal-body">{this.renderBody()}</div>
          {this.renderFooter()}
        </div>
      </>,
      document.body,
    );
  }
}

decorate(ChangeInstanceTypeModal, {
  compatibleConfigs: observable,
  selectedConfigId: observable,
  loading: observable,
  submitting: observable,
  error: observable,
  enableHyperthreading: observable,
  region: computed,
  currentConfig: computed,
  selectedConfig: computed,
  selectableConfigs: computed,
  fetchCompatibleConfigs: action,
  handleSelectConfig: action,
  handleHyperthreadingToggle: action,
  handleConfirm: action,
  handleClose: action,
});

export default observer(ChangeInstanceTypeModal);
